import InsetContainer from 'components/common/containers/inset_container';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';

const NavUl = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;

  &:before {
    background-color: ${p => p.theme.colors.gray400};
    border-radius: 4px;
    bottom: -2px;
    content: '';
    position: absolute;
    top: -2px;
    width: 4px;
    z-index: 1;
  }

  li {
    border-left: 4px solid transparent;
    margin-top: 0;
    padding-left: 30px;
    position: relative;
    z-index: 2;
    cursor: pointer;

    & a {
      font-size: 13px;
      font-style: normal;
      font-weight: normal;
      line-height: 24px;
      color: ${p => p.theme.colors.gray600};
    }

    &.is-active:before {
      background-color: ${p => p.theme.colors.green400};
      border-radius: 4px;
      bottom: 0;
      content: '';
      left: -4px;
      position: absolute;
      top: 0;
      width: 4px;
      z-index: 2;
    }

    &.is-active {
      & a {
        color: ${p => p.theme.colors.green400};
      }
    }
  }
`;

const EVENT_SCROLL = 'scroll';
const EVENT_SCROLL_TO = 'scroll_to';
const EVENT_NAV_SECTION_ACTIVATED = 'nav-section-activated';
const ACTIVE_NAV_CLASS = 'is-active';
const EVENT_ACTIVE_SECTION = 'active_section';

const NavScrollSpyContainer = props => {
  const containerEl = React.useRef();

  const getElement = React.useCallback(() => {
    return props.parentDataAid ? document.querySelector(`[data-aid="${props.parentDataAid}"]`) : containerEl.current;
  }, [props]);

  const sectionActiveClassName = React.useCallback(() => {
    return props.sectionActiveClassName || ACTIVE_NAV_CLASS;
  }, [props]);

  const updateScrollPosition = React.useCallback(
    function() {
      return props.children.map(function(child) {
        if (!child.ref) {
          return null;
        }
        const globalPosition = getElement().pageYOffset || getElement().offsetTop;

        const sectionElement = child.ref.current;
        if (!sectionElement) {
          return null;
        }
        const scrollSectionOffsetTop = sectionElement.offsetTop - globalPosition;
        const currentScrollPosition = getElement().scrollTop;
        const containerHeight = getElement().scrollHeight;

        if (
          currentScrollPosition >= scrollSectionOffsetTop &&
          currentScrollPosition <= scrollSectionOffsetTop + sectionElement.scrollHeight
        ) {
          sectionElement.classList.add(sectionActiveClassName());
          sendUpdateNavLink({ ref: child.ref.current });
        } else {
          sectionElement.classList.remove(sectionActiveClassName());
        }

        const bottomOfTheParent = containerHeight <= getElement().clientHeight + currentScrollPosition;
        if (bottomOfTheParent && currentScrollPosition < scrollSectionOffsetTop) {
          sectionElement.classList.add(ACTIVE_NAV_CLASS);
          sendUpdateNavLink({ ref: child.ref.current });
        }
        return null;
      });
    },
    [props, getElement, sectionActiveClassName]
  );

  const sendUpdateNavLink = ({ ref }) => {
    document.dispatchEvent(new CustomEvent(EVENT_NAV_SECTION_ACTIVATED, { detail: { ref } }));
  };

  const onSectionActive = ref => {
    ref.detail.ref && ref.detail.ref.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    updateScrollPosition();
  });

  useLayoutEffect(() => {
    const currentElement = getElement();
    currentElement.addEventListener(EVENT_SCROLL, updateScrollPosition);
    document.addEventListener(EVENT_SCROLL_TO, onSectionActive);
    return function() {
      currentElement.removeEventListener(EVENT_SCROLL, updateScrollPosition);
      document.removeEventListener(EVENT_SCROLL_TO, onSectionActive);
    };
  }, [updateScrollPosition, getElement]);

  return (
    <NavScrollPane data-aid="navScrollPanel" ref={containerEl}>
      {props.children}
    </NavScrollPane>
  );
};

const NavScrollSpySection = React.forwardRef((props, ref) => {
  const onSectionHasBeenActivated = React.useCallback(() => {
    document.dispatchEvent(new CustomEvent(EVENT_SCROLL_TO, { detail: { ref: ref.current } }));
  }, [ref]);

  useLayoutEffect(() => {
    if (!ref || !ref.current) {
      return;
    }
    let currentElement = ref.current;
    currentElement.addEventListener(EVENT_ACTIVE_SECTION, onSectionHasBeenActivated);
    return () => {
      currentElement && currentElement.removeEventListener(EVENT_ACTIVE_SECTION, onSectionHasBeenActivated);
    };
  }, [ref, props.children, onSectionHasBeenActivated]);

  return (
    <div ref={ref} {...props}>
      {props.children}
    </div>
  );
});

const NavSpyLink = React.forwardRef((props, ref) => {
  const [active, setActive] = React.useState(false);

  const handleClick = event => {
    event.preventDefault();
    event.stopPropagation();

    props.sectionRef.current.dispatchEvent(
      new CustomEvent(EVENT_ACTIVE_SECTION, { detail: { sectionRef: props.sectionRef.current } })
    );
    return false;
  };

  const onSectionActivated = ref => {
    setActive(props.sectionRef.current === ref.detail.ref);
  };

  useEffect(() => {
    document.addEventListener(EVENT_NAV_SECTION_ACTIVATED, ref => {
      onSectionActivated(ref);
    });
    return () => document.removeEventListener(EVENT_NAV_SECTION_ACTIVATED, onSectionActivated);
  });

  return (
    <li className={active ? ACTIVE_NAV_CLASS : null} onClick={handleClick} ref={ref}>
      <a href={props.to}>{props.children}</a>
    </li>
  );
});

/**
 * @visibleName Scroll Spy
 */
const NavScrollSpy = ({ children, className = '', 'data-aid': dataAid = 'navScrollSpy' }) => {
  return (
    <nav aria-labelledby="nav-spy-label" className={className} data-aid={dataAid}>
      <InsetContainer inset="none">
        <NavUl role="navigation">{children}</NavUl>
      </InsetContainer>
    </nav>
  );
};

const NavScrollPane = styled.div`
  height: 100%;
`;

NavScrollSpy.propTypes = {
  /** String to specify a class for section in case of active */
  sectionActiveClassName: PropTypes.string,
  /** String to specify a scrolling container */
  parentDataAid: PropTypes.string,
  /** Child components of the nav spy, usually categories and page links */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  /** String of CSS class names to allow custom styling */
  className: PropTypes.string,
  /** String to specify a data-aid for testing */
  dataAid: PropTypes.string,
};

export { NavScrollSpy, NavScrollSpyContainer, NavScrollSpySection, NavSpyLink };
